import 'reflect-metadata';
import { helperEnabled, preLevelHelperReward } from '../Config/Helpers';
import { GameItemConfig, ItemIDs } from '../Config/Items';
import { ILevelConfig, MapList } from '../Config/Levels';
import GameLoader from '../Core/Assets';
import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../DeltaTracking/AdParamBuilder';
import {
    TGameAreas,
    TransactionActionTypes,
    TransactionTypes,
} from '../DeltaTracking/ResourceParamBuilder';
import { SocialActionTypes } from '../DeltaTracking/SocialParamBuilder';
import { ParamGroups } from '../DeltaTracking/Tracker';
import { PurchaseStatus } from '../SDK/ISDKpayments';
import { ShareableMoments } from '../SDK/SDKmessages';
import { Actions } from '../State/Actions/Actions';
import { purchase } from '../State/reducers/currency';
import { shouldRewardBots } from '../State/reducers/social';
import { Views } from '../State/reducers/view';
import { IGameState } from '../State/Store/store';
import { getSnapShot } from '../Utils/ShareHelpers';
import HudView from '../View/HudView';
import { HudActions, HudEmitter } from './Hud/HudEmitter';
import { Manager } from './Manager';
import { PopupQueue } from './Popups/PopupQueue';
import AdTracking, { VideoAdReasons } from './Tracking/AdTracking';
import BotTracking, { BotTrackStages } from './Tracking/BotTracking';
import FTUETracking, { FTUE_TRACK_STAGES } from './Tracking/FTUETracking';
import ResourceTracking from './Tracking/ResourceTracking';
import ShortcutTracking, { SCTrackStages } from './Tracking/ShortcutTracking';
import SocialTracking from './Tracking/SocialTracking';
import { PrizeWheel } from './Popups/PrizeWheel';
import { SocialHub } from './Popups/SocialHub';
import { Shop } from './Popups/Shop';
import { EmbeddedSprite } from '../UI/Shared/EmbeddedSprite';

const hudLogger: ILogger = getLogger('[HUD-POPUP]');

export interface IPrelevelPayload {
    config: ILevelConfig;
    prelevelId: string;
}

export enum Popup {
    SOCIAL,
    INSUFFICIENT,
    PRELEVEL,
    JOURNAL,
    MAPGATE,
    SHORTCUT,
    BOT,
    PRIZEWHEEL,
    CURRENCY,
    SHAREABLE,
    BOTREWARD,
    SHOP,
    PURCHASE_STATUS,
    INFO,
    FRIEND_JOINED,
    GIFT_RECEIVED,
    MODAL,
    AD_ERROR,
    XPROMO,
}

interface IPopupLayer {
    queue: PopupQueue;
    active: Popup;
}

export class Popups extends Manager {
    public view: HudView;
    public adSuccessAction: () => any;
    public adErrorAction: () => any;
    public shop: Shop;
    private currencyConfirmAction: any;
    private currencyCancelAction: () => any;
    private prelevelData: IPrelevelPayload;
    private layers: IPopupLayer[] = [];
    private prizeWheel: PrizeWheel;
    private socialHub: SocialHub;

    constructor(view: HudView) {
        super();
        this.view = view;
        this.prizeWheel = new PrizeWheel(this.view, this);
        this.socialHub = new SocialHub(this.view, this);
        this.shop = new Shop(this.view);

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleViewChange(state, pre);
        });

        HudEmitter.instance.on(HudActions.ADD_POPUP, this.addPopup.bind(this));
        HudEmitter.instance.on(HudActions.CLOSE_POPUP, this.removePopup.bind(this));
        HudEmitter.instance.on(HudActions.WATCH_REWARDED_AD, this.watchRewardedAd.bind(this));
        HudEmitter.instance.on(HudActions.SHARE_MOMENT, this.handleShareMoment.bind(this));
        HudEmitter.instance.on(HudActions.INVITE_PLAYER, this.handleInvitePlayer.bind(this));
        HudEmitter.instance.on(
            HudActions.SHARE_MOMENT_COMPLETE,
            this.handleShareableComplete.bind(this)
        );
    }

    public cleanUp() {
        super.cleanUp();
    }

    @openPopupAction(Popup.INSUFFICIENT)
    public showInsuffiecientFunds(args: any) {
        if (this.view) {
            Core.store.dispatch(Actions.popupShown());
            const finalAction = () => Core.store.dispatch(Actions.popupDismissed());
            const outOfResource =
                args[0] !== 'heart' ||
                (args[0] === 'heart' && Core.store.getState().StateCurrency.heart <= 0);
            this.view.addCurrencyNeeded(
                args[0],
                Core.store.getState().StateCurrency.coin,
                outOfResource
            );
            this.currencyConfirmAction = (...confirmArgs: Array<unknown>) => {
                args[1](...confirmArgs);
                finalAction();
            };
            this.currencyCancelAction = (...cancelArgs: Array<unknown>) => {
                args[2](...cancelArgs);
                finalAction();
            };
        }
    }

    @openPopupAction(Popup.INFO)
    public handleGamePause() {
        this.view.showInfoPopup(Core.sdk.user.userId);
    }

    @closePopupAction(Popup.INFO)
    public handleSettingsClose() {
        this.view.removeInfoPopup();
    }

    @openPopupAction(Popup.XPROMO)
    public handleXPromoPopup(banner: EmbeddedSprite, gameId: number, gameName: string) {
        this.view.showXPromoPopup(banner, gameId, gameName);
    }

    @closePopupAction(Popup.XPROMO)
    public handleXPromoPopupClose() {
        this.view.removeXPromoPopup();
    }

    @openPopupAction(Popup.SHAREABLE)
    public async handleShareablePopup(type: ShareableMoments, payload: string, flash: boolean) {
        const shareAsset = MapList.includes(payload) ? getSnapShot(payload) : 'fbshare-jackpot';
        this.view.createLoadSocial();
        await GameLoader.loadAssetList({ assetList: [shareAsset] });
        await this.view.removeLoadSocial();
        this.view.addShareablePopup(type, payload, flash);
        SocialTracking.shareShow(type, payload);
    }

    @closePopupAction(Popup.SHAREABLE)
    public async handleShareableComplete(args: any) {
        this.view.removeShareablePopup();
        if (args[1] === ShareableMoments.NEW_MAP) {
            const numUnlocked = Core.store.getState().StatePlayer.unlocked.length;
            const lastMap = Core.store.getState().StatePlayer.unlocked[numUnlocked - 1];
            Core.store.dispatch(Actions.changeMap(lastMap));
            Core.store.dispatch(Actions.changeView(Views.MAP));
        }
    }

    public async handleShareMoment(type: ShareableMoments, payload: string) {
        const shareAsset = MapList.includes(payload) ? getSnapShot(payload) : 'fbshare-jackpot';
        this.view.createLoadSocial();
        await GameLoader.loadAssetList({
            assetList: [shareAsset],
            onProgress: null,
            cb: null,
            oncomplete: () => {},
        });
        Core.sdk.messages
            .share(type, payload)
            .then(() => {
                SocialTracking.shareConfirm(type, payload);
            })
            .catch(err => {
                hudLogger.debug(['error with share moment: ', type, err]);
            })
            .finally(async () => {
                await this.view.removeLoadSocial();
                HudEmitter.instance.emit(HudActions.SHARE_MOMENT_COMPLETE, type, payload);
            });
    }

    public async handleInvitePlayer(callback?: (invited: boolean) => void) {
        this.view.createLoadSocial();
        const result = await Core.sdk.messages.sendInvite();
        await this.view.removeLoadSocial();
        if (callback) {
            callback(result);
        }
    }

    @closePopupAction(Popup.INSUFFICIENT)
    public handleCurrencyClose(args?: string) {
        const confirmed = args && args !== 'close';
        if (args === 'shop') {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.SHOP);
        } else {
            this.view.removeCurrencyNeeded(confirmed, args);
        }
    }

    @closePopupAction(Popup.CURRENCY)
    public handleCurrencyComplete(confirm: boolean, args?: any) {
        if (confirm && this.currencyConfirmAction) {
            this.currencyConfirmAction(args);
        } else if (this.currencyCancelAction) {
            this.currencyCancelAction();
        }
        this.currencyConfirmAction = null;
        this.currencyCancelAction = null;
    }

    @openPopupAction(Popup.PRELEVEL)
    public async showPrelevel(payload: IPrelevelPayload) {
        FTUETracking.track(
            FTUE_TRACK_STAGES.PRELEVEL_SHOWN,
            Core.store.getState(),
            payload.prelevelId
        );
        this.prelevelData = payload;
        const levelId = parseInt(payload.config.levelId.split('-')[0], 10);
        const useHelper = helperEnabled(levelId, preLevelHelperReward.id as ItemIDs);
        const helperFriend = useHelper ? await Core.sdk.messages.getRandomFriend() : null;
        const collection = Core.store.getState().StatePlayer.activeCollection;
        const { levelLeaderboard } = this.view.addPreLevel(
            ['locked', 'locked', 'locked'],
            payload.config,
            helperFriend,
            collection
        );
        const data = await Core.sdk.leaderboards.getLevelLeaderBoardData(levelId.toString());
        levelLeaderboard.setData(data, payload.config.winTime !== 0);
    }

    @closePopupAction(Popup.PRELEVEL)
    public handlePrelevelClose(args: string) {
        this.view.removePrelevel(args, this.handlePrelevelComplete.bind(this));
    }

    public handlePrelevelComplete(args: string) {
        if (this.prelevelData && args === 'play') {
            if (Core.store.getState().StateCurrency.heart > 0) {
                FTUETracking.track(
                    FTUE_TRACK_STAGES.PRELEVEL_CLICK,
                    Core.store.getState(),
                    this.prelevelData.prelevelId
                );
                Core.store.dispatch(Actions.startLevel(Core.store.getState().StateLevel.prelevel));
                this.prelevelData = null;
            } else {
                const self = this;
                this.showInsuffiecientFunds([
                    'heart',
                    async (args2: string) => {
                        if (args2 === 'buy') {
                            await self.purchaseHeart().then(() => {
                                FTUETracking.track(
                                    FTUE_TRACK_STAGES.PRELEVEL_CLICK,
                                    Core.store.getState(),
                                    self.prelevelData.prelevelId
                                );
                                Core.store.dispatch(
                                    Actions.startLevel(Core.store.getState().StateLevel.prelevel)
                                );
                            });
                        } else if (args2 === 'watch') {
                            self.adSuccessAction = () => {
                                AdTracking.trackAdWatched(VideoAdReasons.HEART);
                                Core.store.dispatch(Actions.updateHearts(1));
                                Core.store.dispatch(
                                    Actions.startLevel(Core.store.getState().StateLevel.prelevel)
                                );
                            };
                            self.adErrorAction = () => {
                                Core.store.dispatch(Actions.clearPreLevel());
                            };
                            Core.delta.setAdParams(
                                AdRewardTypes.LIFE,
                                AdContexts.LIFE_NEEDED,
                                AdGameAreas.MAP,
                                -1
                            );
                            await self.watchRewardedAd('life');
                        }
                    },
                    () => {
                        if (self.prelevelData) {
                            FTUETracking.track(
                                FTUE_TRACK_STAGES.PRELEVEL_DISMISSED,
                                Core.store.getState(),
                                self.prelevelData.prelevelId
                            );
                            self.prelevelData = null;
                        }
                        Core.store.dispatch(Actions.clearPreLevel());
                    },
                ]);
            }
        } else {
            if (this.prelevelData) {
                FTUETracking.track(
                    FTUE_TRACK_STAGES.PRELEVEL_DISMISSED,
                    Core.store.getState(),
                    this.prelevelData.prelevelId
                );
            }
            Core.store.dispatch(Actions.clearPreLevel());
            this.prelevelData = null;
        }
    }

    public async purchaseHeart() {
        return new Promise((resolve, reject) => {
            if (purchase(Core.store.getState().StateCurrency, GameItemConfig.heart.price)) {
                const savedDeltaData = Core.delta.trackCurPropGetters.get(ParamGroups.SAVED)();
                Core.delta.setSavedParams({
                    livesBoughtWithCoins: savedDeltaData.livesBoughtWithCoins + 1,
                });
                Core.store.dispatch(Actions.updateCoins(-GameItemConfig.heart.price));
                Core.store.dispatch(Actions.updateHearts(1));
                ResourceTracking.heartBought(); // delta tracked with heart bought

                resolve();
            } else {
                reject();
            }
        });
    }

    // ad tracking params sent before emitter
    // tracking event sent with ad
    @openPopupAction(Popup.CURRENCY)
    public handleCurrencyAdd(id: string) {
        const self = this;
        this.showInsuffiecientFunds([
            id,
            async (action: string) => {
                if (action === 'watch') {
                    if (id === 'coin') {
                        self.adSuccessAction = () => {
                            AdTracking.trackAdWatched(VideoAdReasons.COINS);
                            Core.store.dispatch(Actions.updateCoins(100));
                            HudEmitter.instance.emit(HudActions.SHOW_CURR_ADDS);
                        };

                        // ad tracking params set where add popup call is placed
                        await this.watchRewardedAd('coins');
                    } else if (id === 'heart') {
                        self.adSuccessAction = () => {
                            Core.store.dispatch(Actions.updateHearts(1));
                            HudEmitter.instance.emit(HudActions.SHOW_CURR_ADDS);
                        };
                        // ad tracking params set where add popup call is placed
                        await this.watchRewardedAd('life');
                    }
                }
                if (action === 'buy') {
                    if (purchase(Core.store.getState().StateCurrency, GameItemConfig[id].price)) {
                        Core.store.dispatch(Actions.updateCoins(-GameItemConfig[id].price));
                        Core.store.dispatch(Actions.updateHearts(1));
                        const area =
                            Core.store.getState().StateView === Views.MAP
                                ? TGameAreas.MAP
                                : TGameAreas.LEVEL;
                        ResourceTracking.sendBuyResourceEvent(id, 1, area);
                    }
                }
                if (action === 'ask') {
                    // no tracking here because this is not fully implemented
                    if (id === 'coin') {
                        Core.store.dispatch(Actions.updateCoins(100));
                    } else if (id === 'heart') {
                        Core.store.dispatch(Actions.updateHearts(1));
                    }
                }
            },
            () => {},
        ]);
    }

    @openPopupAction(Popup.SHORTCUT)
    public showShortcutPopup() {
        ShortcutTracking.track(SCTrackStages.SHOW);
        this.view.createShortcutPopup();
    }

    @closePopupAction(Popup.SHORTCUT)
    public handleShortCutComplete(args: string) {
        if (args === 'close') {
            ShortcutTracking.track(SCTrackStages.CLOSE);
            Core.store.dispatch(Actions.shortcutDismissed());
        } else {
            if (args === 'confirm') {
                ShortcutTracking.track(SCTrackStages.ACCEPT);
                const isRewarded = !Core.store.getState().StatePlayer.optInShortcutRewardDate;
                this.nativeShortcutPrompt(isRewarded);
                Core.store.dispatch(Actions.shortcutAccepted());
            }
        }
    }

    public nativeShortcutPrompt(isRewarded: boolean, callback?: () => void) {
        Core.sdk.messages
            .createShortcut()
            .then(() => {
                if (isRewarded) {
                    Core.store.dispatch(Actions.updateCoins(500));
                    // TODO check if we need another param for resource action reward for shortcut
                    ResourceTracking.sendCoinsResourceEvent(
                        500,
                        TransactionTypes.REWARD,
                        TransactionActionTypes.REWARD,
                        TGameAreas.MAP
                    );
                }
                Core.store.dispatch(Actions.shortcutRewarded());
                ShortcutTracking.track(SCTrackStages.CONFIRM);
            })
            .catch(() => {
                hudLogger.debug('did not add the shortcut');
                ShortcutTracking.track(SCTrackStages.DENY);
                if (callback) {
                    callback();
                }
            });
    }

    @openPopupAction(Popup.BOT)
    public showBotPopup() {
        BotTracking.track(BotTrackStages.SHOW);
        this.view.createBotPopup();
    }

    @closePopupAction(Popup.BOT)
    public handleBotComplete(args: string) {
        if (args !== 'confirm') {
            BotTracking.track(BotTrackStages.CLOSE);
            Core.store.dispatch(Actions.optInDismissed());
        } else {
            BotTracking.track(BotTrackStages.ACCEPT);
            const isRewarded = shouldRewardBots(Core.store.getState().StateSocial);
            this.nativeBotPrompt(isRewarded);
            Core.store.dispatch(Actions.optInAccepted());
        }
    }

    @openPopupAction(Popup.MODAL)
    public showModalPopup() {
        this.view.showModal();
    }

    @closePopupAction(Popup.MODAL)
    public handleModalComplete(args: string) {
        this.view.removeModal();
    }

    public nativeBotPrompt(isRewarded: boolean) {
        Core.sdk.bots
            .subscribeBots()
            .then(res => {
                hudLogger.debug(' subscribed to bots Yay!!' + res);

                if (isRewarded) {
                    Core.store.dispatch(Actions.updateCoins(500));
                    Core.store.dispatch(Actions.optInRewarded());

                    ResourceTracking.sendCoinsResourceEvent(
                        500,
                        TransactionTypes.REWARD,
                        TransactionActionTypes.BOT,
                        TGameAreas.MAP
                    );
                }

                BotTracking.track(BotTrackStages.CONFIRM);
            })
            .catch(err => {
                hudLogger.debug(' did not subscribe to bots BOO!!' + err);
                BotTracking.track(BotTrackStages.DENY);
            });
    }

    @openPopupAction(Popup.SHOP)
    public async showShopPopup() {
        this.shop.addShop();
    }

    @closePopupAction(Popup.SHOP)
    public async hideShopPopup() {
        await this.shop.consumePurchases();
        await this.shop.removePopup();
    }

    @openPopupAction(Popup.JOURNAL)
    public showJournalPopup() {
        if (!Core.store.getState().StateMissions.missionsShown) {
            Core.store.dispatch(Actions.missionsShown());
        }

        this.view.createJournalPopup(
            Core.store.getState().StateMissions.missions,
            Core.store.getState().StateMissions.completedCount,
            Core.store.getState().StateMissions.milestone,
            Core.store.getState().StatePlayer.collectablesProgress,
            Core.store.getState().StatePlayer.activeCollection
        );
    }

    @closePopupAction(Popup.JOURNAL)
    public async handleJournalClose() {
        await this.view.deleteJournalPopup();
    }

    @openPopupAction(Popup.MAPGATE)
    public showMapGatePopup() {
        this.view.createMapGatePopup(
            Core.store.getState().StatePlayer.mapId,
            Core.store.getState().StatePlayer.unlocked
        );
    }

    @closePopupAction(Popup.MAPGATE)
    public async handleMapGateComplete(args: string) {
        if (args[0] !== 'close') {
            Core.store.dispatch(Actions.changeMap(args));
            Core.store.dispatch(Actions.changeView(Views.MAP));
        }
    }

    /**
     * shows the socialhubpopup
     * triggered by the player pressing the socialbutton
     */
    @openPopupAction(Popup.SOCIAL)
    public async showSocialPopup() {
        await this.socialHub.addSocialHub();
    }

    @openPopupAction(Popup.PRIZEWHEEL)
    public showPrizeWheelPopup() {
        this.prizeWheel.addPrizeWheel();
    }

    @closePopupAction(Popup.PRIZEWHEEL)
    protected async handlePrizeWheelComplete() {
        await this.view.removePrizeWheelPopup().catch(err => {
            console.log(['error with remove prizewheel popup', err]);
        });
    }

    public async watchRewardedAd(
        type: string,
        success: () => void = this.adSuccessAction,
        error: () => void = this.adErrorAction
    ) {
        this.view.createLoadAdText();
        const adSuccess = () => {
            if (success) {
                success();
            }
            this.view.deleteLoadAdText();
        };

        const adError = () => {
            if (error) {
                error();
            }
            this.view.deleteLoadAdText();
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.AD_ERROR, 2);
        };

        await Core.sdk.ads.showAd('rewarded', type, adSuccess, adError);
    }

    @openPopupAction(Popup.AD_ERROR)
    protected openAdError() {
        this.view.addAdError();
    }

    @closePopupAction(Popup.AD_ERROR)
    protected closeAdError() {
        this.view.removeAdError();
    }

    @openPopupAction(Popup.PURCHASE_STATUS)
    protected openPurchaseStatusPopup(status: PurchaseStatus) {
        this.view.createPurchaseStatusPopup(status);
    }

    @closePopupAction(Popup.PURCHASE_STATUS)
    protected closePurchaseStatusPopup() {
        this.view.removePurchaseStatusPopup();
    }

    @openPopupAction(Popup.BOTREWARD)
    protected async handleBotRewardPopup(args: any) {
        await GameLoader.loadAssetList({ assetList: ['mapui', 'commonui'] });
        const gifts = args ? args : Core.store.getState().StateSocial.gifts;
        this.view.showBotRewardPopup(gifts);
    }

    @closePopupAction(Popup.BOTREWARD)
    protected async handleBotRewardComplete() {
        const gifts = Core.store.getState().StateSocial.gifts;
        gifts.forEach(g => {
            Core.store.dispatch(Actions.recordGift(g));
        });
        this.view.removeBotRewardPopup();
    }
    @closePopupAction(Popup.SOCIAL)
    protected async closeSocialHub(args: any) {}

    @openPopupAction(Popup.FRIEND_JOINED)
    protected handleFriendJoined(player: any) {
        // this.view.createPurchaseStatusPopup(status);
        this.view.createFriendJoinedPopup(player);
    }

    @closePopupAction(Popup.FRIEND_JOINED)
    protected handleFriendJoinedComplete(args: any) {
        if (args === 'close') {
            this.view.removeFriendJoinedPopup().catch(err => {
                console.log(['error remove friendJoinedPopup', err]);
            });
            Core.store.dispatch(Actions.updateCoins(500));
            ResourceTracking.sendCoinsResourceEvent(
                500,
                TransactionTypes.REWARD,
                TransactionActionTypes.SOCIAL,
                TGameAreas.MAP,
                SocialActionTypes.INVITE
            );
        }
    }

    @openPopupAction(Popup.GIFT_RECEIVED)
    protected async handleGiftReceived(friendId: string) {
        const friends = await Core.sdk.messages.getFriends();
        const friendData = friends.find((friend: any) => friend.userId === friendId);
        this.view.createGiftReceivedPopup(friendData);
    }

    @closePopupAction(Popup.GIFT_RECEIVED)
    protected handleGiftReceivedComplete(args: any) {
        if (args === 'close') {
            this.view.removeGiftReceivedPopup().catch(err => {
                console.log(['error remove gift recieved popup', err]);
            });
            HudEmitter.instance.emit(HudActions.SHOW_CURR_ADDS);
        }
    }

    private handleViewChange(state: IGameState, pre: IGameState) {
        const {
            StatePlayer: player,
            StateSocial: social,
            StateView: stateView,
        } = Core.store.getState();

        const { StateView: preView } = pre;

        if (stateView !== preView) {
            if (stateView === Views.MAP) {
                if (social.nativeOptInRequired) {
                    this.nativeBotPrompt(false);
                    Core.store.dispatch(Actions.optInShown());
                }

                if (player.nativeOptInShortcutRequired) {
                    this.nativeShortcutPrompt(false, () => {
                        Core.sdk.messages.canCreateShortcut().then(canCreate => {
                            if (canCreate && !player.optInShortcutRewardDate) {
                                // TODO need ui here
                                // this.hud.ui.addShortcutBtn();
                            }
                        });
                        Core.store.dispatch(Actions.optInShortcutShown());
                    });
                }
            }
        }
    }

    private addPopup(type: Popup, layer: number = 0, ...args: Array<unknown>) {
        while (this.layers.length < layer + 1) {
            this.layers.push({
                queue: new PopupQueue(),
                active: null,
            });
        }
        const currentLayer = this.layers[layer];

        const displayed = currentLayer.queue.queue({
            type,
            openAction: {
                function: this.getPopupAction('openAction', type),
                args,
            },
        });

        if (displayed) {
            currentLayer.active = type;
        }
    }

    private async removePopup(type: Popup, layer: number = 0, ...args: Array<unknown>) {
        if (layer === -1) {
            layer = this.layers.length - 1;
        }
        /*       if (this.layers[layer].active !== type) {
            return;
        }*/
        await this.getPopupAction('closeAction', type)(...args);
        HudEmitter.instance.emit(HudActions.POPUP_CLOSED, type, layer, ...args);
        const empty = this.layers[layer].queue.processQueue();
        if (!empty) {
            this.layers[layer].active = this.layers[layer].queue.current.type;
        }
        while (
            layer > 0 &&
            this.layers.length === layer + 1 &&
            this.layers[layer].queue.length === 0
        ) {
            this.layers.pop();
            layer--;
        }
    }

    private getPopupAction(action: 'openAction' | 'closeAction', type: Popup) {
        const methodName = Reflect.getMetadata(`${action}${type}`, this);
        // @ts-ignore
        return this[methodName].bind(this);
    }
}

function closePopupAction(type: Popup) {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
        Reflect.defineMetadata(`closeAction${type}`, propertyKey, Object.getPrototypeOf(target));
    };
}

function openPopupAction(type: Popup) {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
        Reflect.defineMetadata(`openAction${type}`, propertyKey, Object.getPrototypeOf(target));
    };
}
